import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
    Search, FileText, CheckSquare, Tag, Code, Copy, Check, ChevronRight, AlertCircle, Clock, RotateCcw,
    ShieldCheck, ExternalLink, XCircle, CheckCircle, Layers, ArrowRight,
    TrendingUp, RefreshCw, Wifi, WifiOff, Activity, MapPin
} from 'lucide-react';
import {
    GlassCard, GlassButton, GlassInput, GlassSelect, GlassTabs, GlassBadge, ModuleHeader
} from '../ui';
import {
    getBrandGuides, BrandGuide,
    seoKeywords, seoContentBrief, seoCheck, seoMetaTags, seoSchema,
    seoSaveSearch, seoListSearches, seoGetIndexability, createStream,
    gscGetAuthUrl, gscGetStatus, gscListSites, gscConnect, gscSync, gscGetSnapshots, gscDisconnect,
    getSchemaCoverage, SchemaCoverageResponse,
    SEOKeywordResult, SEOContentBrief, SEOCheckResult, SEOMetaTags, SEOSchema, SeoSearchRecord,
    SeoPageIndexabilityRecord, GscStatus, GscSnapshots,
    runSeoAudit, getSeoAudit, listSeoAuditHistory, SeoAuditRecord, SeoAuditHistoryItem,
    getMigrationCheck, MigrationCheckResult,
} from '../../services/api';
import AuditReport from './AuditReport';
import LowCtrPanel from './LowCtrPanel';
import CannibalizationPanel from './CannibalizationPanel';
import AIReferrerPanel from './AIReferrerPanel';
import StrikingDistancePanel from './StrikingDistancePanel';
import IndexCoveragePanel from './IndexCoveragePanel';
import GbpCategoryGapPanel from './GbpCategoryGapPanel';
import CitationAuditPanel from './CitationAuditPanel';
import './SEO.css';

const getApiError = (e: any, fallback: string): string => {
    const detail = e?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
    return fallback;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
    { id: 'keywords', label: 'Keyword Research' },
    { id: 'brief', label: 'Content Brief' },
    { id: 'checker', label: 'SEO Checker' },
    { id: 'meta', label: 'Meta Tags' },
    { id: 'schema', label: 'Schema Generator' },
    { id: 'audit', label: 'Site Audit' },
    { id: 'indexability', label: 'Indexability' },
    { id: 'performance', label: 'Performance' },
    { id: 'opportunities', label: 'Opportunities' },
    { id: 'cannibalization', label: 'Cannibalization' },
    { id: 'ai-referrals', label: 'AI Referrals' },
    { id: 'index-coverage', label: 'Index Coverage' },
    { id: 'local-categories', label: 'Local Categories' },
    { id: 'citations', label: 'Citations' },
];

const VALID_TAB_IDS = new Set(TABS.map(t => t.id));

const TAB_ICONS: Record<string, React.ReactNode> = {
    keywords: <Search size={12} />,
    brief: <FileText size={12} />,
    checker: <CheckSquare size={12} />,
    meta: <Tag size={12} />,
    schema: <Code size={12} />,
    audit: <Activity size={12} />,
    'ai-referrals': <Activity size={12} />,
    'local-categories': <MapPin size={12} />,
    'citations': <MapPin size={12} />,
};

const SCHEMA_TYPES = [
    { value: 'article', label: 'Article' },
    { value: 'faq', label: 'FAQ' },
    { value: 'local_business', label: 'Local Business' },
];

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function getScoreClass(score: number): string {
    if (score >= 80) return 'seo-score-excellent';
    if (score >= 60) return 'seo-score-good';
    if (score >= 40) return 'seo-score-fair';
    return 'seo-score-poor';
}
function getScoreLabel(score: number): string {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Needs Work';
    return 'Poor';
}
function getBarColor(score: number): string {
    if (score >= 80) return '#059669';
    if (score >= 60) return '#2563eb';
    if (score >= 40) return '#b45309';
    return '#dc2626';
}
function getCompetitionClass(competition: string): string {
    const c = competition?.toLowerCase();
    if (c === 'low') return 'seo-competition-low';
    if (c === 'high') return 'seo-competition-high';
    return 'seo-competition-medium';
}
function relativeTime(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

const LoadingState: React.FC<{ message?: string }> = ({ message = 'Generating with AI...' }) => (
    <div className="seo-loading">
        <div className="seo-spinner" />
        <span>{message}</span>
    </div>
);
const ErrorState: React.FC<{ message: string }> = ({ message }) => (
    <div className="seo-error">
        <AlertCircle size={16} />
        <span>{message}</span>
    </div>
);
const CopyButton: React.FC<{ text: string }> = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = useCallback(async () => {
        try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
    }, [text]);
    return (
        <button className={`seo-copy-btn ${copied ? 'copied' : ''}`} onClick={handleCopy} title="Copy to clipboard">
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'Copied' : 'Copy'}
        </button>
    );
};

// ---------------------------------------------------------------------------
// Tab: Keyword Research
// ---------------------------------------------------------------------------

interface KeywordResearchTabProps {
    savedResult: SEOKeywordResult | null;
    savedQuery: Record<string, any> | null;
    projectId: string;
    onResult: (query: Record<string, any>, result: SEOKeywordResult) => void;
    onPromoteKeyword: (keyword: string, industry: string) => void;
}

const KeywordResearchTab: React.FC<KeywordResearchTabProps> = ({ savedResult, savedQuery, projectId, onResult, onPromoteKeyword }) => {
    const [topic, setTopic] = useState(savedQuery?.topic || '');
    const [industry, setIndustry] = useState(savedQuery?.industry || '');
    const [location, setLocation] = useState(savedQuery?.location || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<SEOKeywordResult | null>(savedResult);
    const [streamStatus, setStreamStatus] = useState<'idle' | 'creating' | 'done' | 'exists'>('idle');

    useEffect(() => {
        if (savedResult) setResult(savedResult);
        if (savedQuery) {
            setTopic(savedQuery.topic || '');
            setIndustry(savedQuery.industry || '');
            setLocation(savedQuery.location || '');
        }
        setStreamStatus('idle');
    }, [savedResult, savedQuery]);

    const handleSubmit = async () => {
        if (!topic.trim() || !industry.trim()) { setError('Topic and Industry are required.'); return; }
        setError(null); setLoading(true); setStreamStatus('idle');
        try {
            const data = await seoKeywords({ topic: topic.trim(), industry: industry.trim(), location: location.trim() || undefined });
            setResult(data);
            onResult({ topic: topic.trim(), industry: industry.trim(), location: location.trim() || undefined }, data);
        } catch (err: any) {
            setError(getApiError(err, 'Keyword research failed. Please try again.'));
        } finally { setLoading(false); }
    };

    const handleCreateStream = async () => {
        if (!topic.trim() || !projectId) return;
        setStreamStatus('creating');
        try {
            await createStream(projectId, topic.trim());
            setStreamStatus('done');
        } catch (err: any) {
            if (err?.response?.status === 409) {
                setStreamStatus('exists');
            } else {
                setStreamStatus('idle');
                setError('Failed to create stream. Please try again.');
            }
        }
    };

    return (
        <>
            <GlassCard className="seo-form-card">
                <div className="seo-form-grid">
                    <div className="seo-form-group">
                        <label>Topic *</label>
                        <GlassInput placeholder="e.g. content marketing" value={topic} onChange={setTopic} />
                    </div>
                    <div className="seo-form-group">
                        <label>Industry *</label>
                        <GlassInput placeholder="e.g. digital marketing" value={industry} onChange={setIndustry} />
                    </div>
                    <div className="seo-form-group">
                        <label>Location (optional)</label>
                        <GlassInput placeholder="e.g. Australia" value={location} onChange={setLocation} />
                    </div>
                </div>
                {error && <ErrorState message={error} />}
                <div className="seo-form-actions">
                    <GlassButton variant="primary" onClick={handleSubmit} disabled={loading}>
                        <Search size={16} />{loading ? 'Researching...' : 'Research Keywords'}
                    </GlassButton>
                </div>
                {loading && <LoadingState message="Researching keywords with AI..." />}
            </GlassCard>

            {result && !loading && (
                <GlassCard className="seo-results-card">
                    {/* Cluster actions header */}
                    <div className="seo-cluster-actions">
                        <div className="seo-cluster-label">
                            <Layers size={14} />
                            <span>Keyword cluster: <strong>{topic}</strong></span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {streamStatus === 'done' && (
                                <span className="seo-stream-badge seo-stream-badge--ok">
                                    <Check size={12} /> Stream created
                                </span>
                            )}
                            {streamStatus === 'exists' && (
                                <span className="seo-stream-badge seo-stream-badge--exists">
                                    Stream already exists
                                </span>
                            )}
                            <GlassButton
                                variant="secondary"
                                onClick={handleCreateStream}
                                disabled={streamStatus === 'creating' || streamStatus === 'done'}
                            >
                                <Layers size={14} />
                                {streamStatus === 'creating' ? 'Creating...' : 'Create Content Stream'}
                            </GlassButton>
                        </div>
                    </div>

                    <div className="seo-results-section">
                        <h3>Primary Keywords ({result.primary_keywords?.length || 0})</h3>
                        <table className="seo-keyword-table">
                            <thead><tr><th>Keyword</th><th>Search Intent</th><th>Competition</th><th>Notes</th><th></th></tr></thead>
                            <tbody>
                                {(result.primary_keywords || []).map((kw, i) => (
                                    <tr key={i}>
                                        <td style={{ fontWeight: 600 }}>{kw.keyword}</td>
                                        <td><GlassBadge variant="info">{kw.search_intent}</GlassBadge></td>
                                        <td>{kw.competition && <span className={`seo-competition-badge ${getCompetitionClass(kw.competition)}`}>{kw.competition}</span>}</td>
                                        <td style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{kw.notes}</td>
                                        <td>
                                            <button
                                                className="seo-promote-btn"
                                                onClick={() => onPromoteKeyword(kw.keyword, industry)}
                                                title="Promote to Content Brief"
                                            >
                                                <FileText size={12} />
                                                <ArrowRight size={10} />
                                                Brief
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="seo-results-section">
                        <h3>Secondary Keywords ({result.secondary_keywords?.length || 0})</h3>
                        <div className="seo-tag-list">{(result.secondary_keywords || []).map((kw, i) => <span key={i} className="seo-tag" title={kw.notes}>{kw.keyword}</span>)}</div>
                    </div>
                    <div className="seo-results-section">
                        <h3>Long-tail Keywords ({result.long_tail_keywords?.length || 0})</h3>
                        <div className="seo-tag-list">{(result.long_tail_keywords || []).map((kw, i) => <span key={i} className="seo-tag" title={kw.notes}>{kw.keyword}</span>)}</div>
                    </div>
                    <div className="seo-results-section">
                        <h3>Questions People Ask ({result.questions_people_ask?.length || 0})</h3>
                        <ul className="seo-question-list">{(result.questions_people_ask || []).map((q, i) => <li key={i}>{q}</li>)}</ul>
                    </div>
                </GlassCard>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Tab: Content Brief
// ---------------------------------------------------------------------------

interface ContentBriefTabProps {
    savedResult: SEOContentBrief | null;
    savedQuery: Record<string, any> | null;
    projectId: string;
    onResult: (query: Record<string, any>, result: SEOContentBrief) => void;
}

const ContentBriefTab: React.FC<ContentBriefTabProps> = ({ savedResult, savedQuery, projectId, onResult }) => {
    const [keyword, setKeyword] = useState(savedQuery?.keyword || '');
    const [industry, setIndustry] = useState(savedQuery?.industry || '');
    const [brandGuideId, setBrandGuideId] = useState(savedQuery?.brand_guide_id || '');
    const [brandGuides, setBrandGuides] = useState<BrandGuide[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<SEOContentBrief | null>(savedResult);

    useEffect(() => { getBrandGuides().then(setBrandGuides).catch(() => {}); }, []);
    useEffect(() => {
        if (savedResult) setResult(savedResult);
        if (savedQuery) {
            setKeyword(savedQuery.keyword || '');
            setIndustry(savedQuery.industry || '');
            setBrandGuideId(savedQuery.brand_guide_id || '');
        }
        // Auto-submit when promoted from keyword research (has query but no result)
        if (savedQuery?.keyword && savedQuery?.industry && !savedResult) {
            const kw = savedQuery.keyword.trim();
            const ind = savedQuery.industry.trim();
            if (kw && ind) {
                setLoading(true);
                seoContentBrief({ keyword: kw, industry: ind, project_id: projectId || undefined })
                    .then(data => { setResult(data); onResult({ keyword: kw, industry: ind }, data); })
                    .catch(() => {})
                    .finally(() => setLoading(false));
            }
        }
    }, [savedResult, savedQuery]); // eslint-disable-line react-hooks/exhaustive-deps

    const brandGuideOptions = [{ value: '', label: 'No brand guide' }, ...brandGuides.map(bg => ({ value: bg.brand_guide_id, label: bg.brand_guide_name }))];

    const handleSubmit = async () => {
        if (!keyword.trim() || !industry.trim()) { setError('Target keyword and Industry are required.'); return; }
        setError(null); setLoading(true);
        try {
            const data = await seoContentBrief({ keyword: keyword.trim(), industry: industry.trim(), brand_guide_id: brandGuideId || undefined, project_id: projectId || undefined });
            setResult(data);
            onResult({ keyword: keyword.trim(), industry: industry.trim(), brand_guide_id: brandGuideId || undefined }, data);
        } catch (err: any) {
            setError(getApiError(err, 'Content brief generation failed. Please try again.'));
        } finally { setLoading(false); }
    };

    return (
        <>
            <GlassCard className="seo-form-card">
                <div className="seo-form-grid">
                    <div className="seo-form-group"><label>Target Keyword *</label><GlassInput placeholder="e.g. seo for small business" value={keyword} onChange={setKeyword} /></div>
                    <div className="seo-form-group"><label>Industry *</label><GlassInput placeholder="e.g. digital marketing agency" value={industry} onChange={setIndustry} /></div>
                    <div className="seo-form-group"><label>Brand Guide (optional)</label><GlassSelect options={brandGuideOptions} value={brandGuideId} onChange={setBrandGuideId} placeholder="Select brand guide..." /></div>
                </div>
                {error && <ErrorState message={error} />}
                <div className="seo-form-actions"><GlassButton variant="primary" onClick={handleSubmit} disabled={loading}><FileText size={16} />{loading ? 'Generating...' : 'Generate Brief'}</GlassButton></div>
                {loading && <LoadingState message="Generating content brief with AI..." />}
            </GlassCard>
            {result && !loading && (
                <GlassCard className="seo-results-card">
                    <div className="seo-results-section">
                        <h3>Brief Overview</h3>
                        <div className="seo-brief-meta">
                            <div className="seo-brief-meta-item"><span className="seo-brief-meta-label">Suggested Title</span><span className="seo-brief-meta-value">{result.title}</span></div>
                            <div className="seo-brief-meta-item"><span className="seo-brief-meta-label">Target Word Count</span><span className="seo-brief-meta-value">{result.target_word_count?.toLocaleString() || '—'} words</span></div>
                            <div className="seo-brief-meta-item" style={{ gridColumn: '1 / -1' }}><span className="seo-brief-meta-label">Meta Description</span><span className="seo-brief-meta-value">{result.meta_description}</span></div>
                        </div>
                    </div>
                    <div className="seo-results-section">
                        <h3>Heading Structure ({result.heading_structure?.length || 0} headings)</h3>
                        <ul className="seo-heading-outline">
                            {(result.heading_structure || []).map((h, i) => (
                                <li key={i} className="seo-heading-item" style={{ paddingLeft: h.level === 'H3' ? 24 : h.level === 'H2' ? 12 : 0 }}>
                                    <span className="seo-heading-level">{h.level}</span><span>{h.text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="seo-results-section"><h3>Secondary Keywords</h3><div className="seo-tag-list">{(result.secondary_keywords || []).map((kw, i) => <span key={i} className="seo-tag">{kw}</span>)}</div></div>
                    <div className="seo-results-section"><h3>Questions to Answer</h3><ul className="seo-question-list">{(result.questions_to_answer || []).map((q, i) => <li key={i}>{q}</li>)}</ul></div>
                    {result.content_tips && result.content_tips.length > 0 && <div className="seo-results-section"><h3>Content Tips</h3><ul className="seo-question-list">{result.content_tips.map((tip, i) => <li key={i}>{tip}</li>)}</ul></div>}
                    {result.competitor_angles && result.competitor_angles.length > 0 && <div className="seo-results-section"><h3>Competitor Angles</h3><ul className="seo-question-list">{result.competitor_angles.map((angle, i) => <li key={i}>{angle}</li>)}</ul></div>}
                </GlassCard>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Tab: SEO Checker
// ---------------------------------------------------------------------------

interface SEOCheckerTabProps {
    savedResult: SEOCheckResult | null;
    savedQuery: Record<string, any> | null;
    onResult: (query: Record<string, any>, result: SEOCheckResult) => void;
}

const SEOCheckerTab: React.FC<SEOCheckerTabProps> = ({ savedResult, savedQuery, onResult }) => {
    const [content, setContent] = useState(savedQuery?.content || '');
    const [targetKeyword, setTargetKeyword] = useState(savedQuery?.target_keyword || '');
    const [metaTitle, setMetaTitle] = useState(savedQuery?.meta_title || '');
    const [metaDescription, setMetaDescription] = useState(savedQuery?.meta_description || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<SEOCheckResult | null>(savedResult);

    useEffect(() => {
        if (savedResult) setResult(savedResult);
        if (savedQuery) { setContent(savedQuery.content || ''); setTargetKeyword(savedQuery.target_keyword || ''); setMetaTitle(savedQuery.meta_title || ''); setMetaDescription(savedQuery.meta_description || ''); }
    }, [savedResult, savedQuery]);

    const handleSubmit = async () => {
        if (!content.trim() || !targetKeyword.trim()) { setError('Content and Target Keyword are required.'); return; }
        setError(null); setLoading(true);
        try {
            const q = { content: content.trim(), target_keyword: targetKeyword.trim(), meta_title: metaTitle.trim() || undefined, meta_description: metaDescription.trim() || undefined };
            const data = await seoCheck(q);
            setResult(data);
            onResult(q, data);
        } catch (err: any) {
            setError(getApiError(err, 'SEO analysis failed. Please try again.'));
        } finally { setLoading(false); }
    };

    const metrics = result ? [
        { key: 'keyword_density', title: 'Keyword Density', data: result.keyword_density },
        { key: 'heading_structure', title: 'Heading Structure', data: result.heading_structure },
        { key: 'meta_title', title: 'Meta Title', data: result.meta_title },
        { key: 'meta_description', title: 'Meta Description', data: result.meta_description },
        { key: 'readability', title: 'Readability', data: result.readability },
        { key: 'ai_citability', title: 'AI Citability', data: result.ai_citability },
    ] : [];

    return (
        <>
            <GlassCard className="seo-form-card">
                <div className="seo-form-group"><label>Content *</label><textarea className="tall" placeholder="Paste your article or page content here..." value={content} onChange={e => setContent(e.target.value)} /></div>
                <div className="seo-form-grid">
                    <div className="seo-form-group"><label>Target Keyword *</label><GlassInput placeholder="e.g. seo best practices" value={targetKeyword} onChange={setTargetKeyword} /></div>
                    <div className="seo-form-group"><label>Meta Title (optional)</label><GlassInput placeholder="Your page title..." value={metaTitle} onChange={setMetaTitle} /></div>
                    <div className="seo-form-group"><label>Meta Description (optional)</label><GlassInput placeholder="Your meta description..." value={metaDescription} onChange={setMetaDescription} /></div>
                </div>
                {error && <ErrorState message={error} />}
                <div className="seo-form-actions"><GlassButton variant="primary" onClick={handleSubmit} disabled={loading}><CheckSquare size={16} />{loading ? 'Analysing...' : 'Check SEO'}</GlassButton></div>
                {loading && <LoadingState message="Analysing SEO with AI..." />}
            </GlassCard>
            {result && !loading && (
                <GlassCard className="seo-results-card">
                    <div className="seo-score-hero">
                        <span className={`seo-score-number ${getScoreClass(result.overall_score)}`}>{result.overall_score}</span>
                        <span className="seo-score-label">{getScoreLabel(result.overall_score)} — Overall SEO Score</span>
                    </div>
                    <div className="seo-metrics-grid">
                        {metrics.map(m => (
                            <GlassCard key={m.key} className="seo-metric-card">
                                <div className="seo-metric-header"><span className="seo-metric-title">{m.title}</span><span className="seo-metric-score">{m.data?.score ?? '—'}</span></div>
                                <div className="seo-metric-bar-bg"><div className="seo-metric-bar-fill" style={{ width: `${m.data?.score ?? 0}%`, background: getBarColor(m.data?.score ?? 0) }} /></div>
                                <p className="seo-metric-notes">{m.data?.notes}</p>
                                {m.key === 'ai_citability' && (m.data as any)?.checks && (
                                    <ul className="seo-recommendations-list">
                                        {Object.entries((m.data as any).checks as Record<string, boolean>).map(([check, pass]) => (
                                            <li key={check}><ChevronRight size={14} className="seo-rec-icon" /><span>{check.replace(/_/g, ' ')}: {pass ? 'Yes' : 'No'}</span></li>
                                        ))}
                                    </ul>
                                )}
                            </GlassCard>
                        ))}
                    </div>
                    {result.recommendations && result.recommendations.length > 0 && (
                        <div className="seo-results-section"><h3>Recommendations</h3>
                            <ul className="seo-recommendations-list">{result.recommendations.map((rec, i) => <li key={i}><ChevronRight size={14} className="seo-rec-icon" /><span>{rec}</span></li>)}</ul>
                        </div>
                    )}
                </GlassCard>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Tab: Meta Tags
// ---------------------------------------------------------------------------

interface MetaTagsTabProps {
    savedResult: SEOMetaTags | null;
    savedQuery: Record<string, any> | null;
    onResult: (query: Record<string, any>, result: SEOMetaTags) => void;
}

const MetaTagsTab: React.FC<MetaTagsTabProps> = ({ savedResult, savedQuery, onResult }) => {
    const [title, setTitle] = useState(savedQuery?.title || '');
    const [content, setContent] = useState(savedQuery?.content || '');
    const [targetKeyword, setTargetKeyword] = useState(savedQuery?.target_keyword || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<SEOMetaTags | null>(savedResult);

    useEffect(() => {
        if (savedResult) setResult(savedResult);
        if (savedQuery) { setTitle(savedQuery.title || ''); setContent(savedQuery.content || ''); setTargetKeyword(savedQuery.target_keyword || ''); }
    }, [savedResult, savedQuery]);

    const handleSubmit = async () => {
        if (!title.trim() || !content.trim()) { setError('Title and Content are required.'); return; }
        setError(null); setLoading(true);
        try {
            const q = { title: title.trim(), content: content.trim(), target_keyword: targetKeyword.trim() || undefined };
            const data = await seoMetaTags(q);
            setResult(data);
            onResult(q, data);
        } catch (err: any) {
            setError(getApiError(err, 'Meta tag generation failed. Please try again.'));
        } finally { setLoading(false); }
    };

    const metaCards = result ? [
        { label: 'Meta Title', value: result.meta_title, limit: 60, icon: <Tag size={13} /> },
        { label: 'Meta Description', value: result.meta_description, limit: 160, icon: <FileText size={13} /> },
        { label: 'OG Title', value: result.og_title, limit: 95, icon: <Tag size={13} /> },
        { label: 'OG Description', value: result.og_description, limit: 200, icon: <FileText size={13} /> },
        { label: 'Twitter Title', value: result.twitter_title, limit: 70, icon: <Tag size={13} /> },
        { label: 'Twitter Description', value: result.twitter_description, limit: 200, icon: <FileText size={13} /> },
    ] : [];

    return (
        <>
            <GlassCard className="seo-form-card">
                <div className="seo-form-group"><label>Page Title *</label><GlassInput placeholder="Your page title..." value={title} onChange={setTitle} /></div>
                <div className="seo-form-group"><label>Content *</label><textarea placeholder="Paste your article or page content here..." value={content} onChange={e => setContent(e.target.value)} /></div>
                <div className="seo-form-group"><label>Target Keyword (optional)</label><GlassInput placeholder="e.g. seo tips for small business" value={targetKeyword} onChange={setTargetKeyword} /></div>
                {error && <ErrorState message={error} />}
                <div className="seo-form-actions"><GlassButton variant="primary" onClick={handleSubmit} disabled={loading}><Tag size={16} />{loading ? 'Generating...' : 'Generate Tags'}</GlassButton></div>
                {loading && <LoadingState message="Generating meta tags with AI..." />}
            </GlassCard>
            {result && !loading && (
                <GlassCard className="seo-results-card">
                    <div className="seo-meta-cards">
                        {metaCards.map((card, i) => (
                            <GlassCard key={i} className="seo-meta-card">
                                <div className="seo-meta-card-header">
                                    <span className="seo-meta-card-label">{card.icon}{card.label}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span className={`seo-meta-char-count ${card.value?.length > card.limit ? 'over-limit' : ''}`}>{card.value?.length || 0} / {card.limit}</span>
                                        <CopyButton text={card.value || ''} />
                                    </div>
                                </div>
                                <p className="seo-meta-card-value">{card.value}</p>
                            </GlassCard>
                        ))}
                    </div>
                </GlassCard>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Tab: Schema Generator
// ---------------------------------------------------------------------------

interface SchemaGeneratorTabProps {
    savedResult: SEOSchema | null;
    savedQuery: Record<string, any> | null;
    onResult: (query: Record<string, any>, result: SEOSchema) => void;
}

const SchemaGeneratorTab: React.FC<SchemaGeneratorTabProps> = ({ savedResult, savedQuery, onResult }) => {
    const [schemaType, setSchemaType] = useState(savedQuery?.schema_type || 'article');
    const [content, setContent] = useState(savedQuery?.content || '');
    const [businessName, setBusinessName] = useState(savedQuery?.business_name || '');
    const [articleTitle, setArticleTitle] = useState(savedQuery?.title || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<SEOSchema | null>(savedResult);

    useEffect(() => {
        if (savedResult) setResult(savedResult);
        if (savedQuery) { setSchemaType(savedQuery.schema_type || 'article'); setContent(savedQuery.content || ''); setBusinessName(savedQuery.business_name || ''); setArticleTitle(savedQuery.title || ''); }
    }, [savedResult, savedQuery]);

    const handleSubmit = async () => {
        if (!content.trim()) { setError('Content is required.'); return; }
        if (schemaType === 'local_business' && !businessName.trim()) { setError('Business name is required for Local Business schema.'); return; }
        setError(null); setLoading(true);
        try {
            const q = { schema_type: schemaType, content: content.trim(), business_name: businessName.trim() || undefined, title: articleTitle.trim() || undefined };
            const data = await seoSchema(q);
            setResult(data);
            onResult(q, data);
        } catch (err: any) {
            setError(getApiError(err, 'Schema generation failed. Please try again.'));
        } finally { setLoading(false); }
    };

    return (
        <>
            <GlassCard className="seo-form-card">
                <div className="seo-form-group"><label>Schema Type *</label><GlassSelect options={SCHEMA_TYPES} value={schemaType} onChange={val => { setSchemaType(val); setResult(null); }} /></div>
                {schemaType === 'local_business' && <div className="seo-form-group"><label>Business Name *</label><GlassInput placeholder="Your business name..." value={businessName} onChange={setBusinessName} /></div>}
                {schemaType === 'article' && <div className="seo-form-group"><label>Article Title (optional)</label><GlassInput placeholder="Article title..." value={articleTitle} onChange={setArticleTitle} /></div>}
                <div className="seo-form-group">
                    <label>Content *</label>
                    <textarea className="tall" placeholder={schemaType === 'faq' ? 'Paste content with questions and answers...' : schemaType === 'local_business' ? 'Paste business description, address, phone number, hours, etc...' : 'Paste your article content here...'} value={content} onChange={e => setContent(e.target.value)} />
                </div>
                {error && <ErrorState message={error} />}
                <div className="seo-form-actions"><GlassButton variant="primary" onClick={handleSubmit} disabled={loading}><Code size={16} />{loading ? 'Generating...' : 'Generate Schema'}</GlassButton></div>
                {loading && <LoadingState message="Generating JSON-LD schema with AI..." />}
            </GlassCard>
            {result && !loading && (
                <GlassCard className="seo-results-card">
                    <div className="seo-results-section">
                        <h3>JSON-LD Schema — {SCHEMA_TYPES.find(t => t.value === result.schema_type)?.label || result.schema_type}</h3>
                        <div className="seo-schema-output">
                            <pre className="seo-schema-code">{result.json_ld}</pre>
                            <div className="seo-schema-actions"><CopyButton text={result.json_ld || ''} /></div>
                        </div>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '4px 0 0' }}>Paste this code into the &lt;head&gt; section of your HTML page.</p>
                    </div>
                </GlassCard>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Tab: Indexability Report Card
// ---------------------------------------------------------------------------

const PAGE_TYPE_LABELS: Record<string, string> = {
    landing_page: 'Landing Page',
    site_builder: 'Site Builder',
    wordpress: 'WordPress',
};

const IndexabilityTab: React.FC<{ projectId: string }> = ({ projectId }) => {
    const [records, setRecords] = useState<SeoPageIndexabilityRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        seoGetIndexability(projectId)
            .then(setRecords)
            .catch(() => setError('Failed to load indexability data.'))
            .finally(() => setLoading(false));
    }, [projectId]);

    const total = records.length;
    const allOk = records.filter(r => r.canonical_ok && r.robots_ok && r.sitemap_ok).length;

    const Tick: React.FC<{ ok: boolean }> = ({ ok }) => ok
        ? <CheckCircle size={16} style={{ color: 'var(--success)' }} />
        : <XCircle size={16} style={{ color: 'var(--danger)' }} />;

    if (loading) return <LoadingState message="Checking indexability..." />;
    if (error) return <ErrorState message={error} />;

    return (
        <>
            {/* Summary header */}
            <GlassCard className="seo-index-summary">
                <div className="seo-index-summary-inner">
                    <ShieldCheck size={32} style={{ color: allOk === total && total > 0 ? 'var(--success)' : 'var(--warning, #f59e0b)' }} />
                    <div>
                        <div className="seo-index-summary-title">
                            {total === 0
                                ? 'No published pages yet'
                                : allOk === total
                                ? `All ${total} published page${total !== 1 ? 's' : ''} fully guaranteed`
                                : `${allOk} of ${total} pages fully guaranteed`}
                        </div>
                        <div className="seo-index-summary-sub">
                            Canonical URL · Robots directive · Sitemap entry — applied at publish time
                        </div>
                    </div>
                </div>
            </GlassCard>

            {total === 0 ? (
                <GlassCard style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-secondary)' }}>
                    <ShieldCheck size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
                    <p>Publish a landing page or site to see its indexability status here.</p>
                </GlassCard>
            ) : (
                <GlassCard className="seo-results-card">
                    <table className="seo-keyword-table seo-index-table">
                        <thead>
                            <tr>
                                <th>Page</th>
                                <th>Type</th>
                                <th style={{ textAlign: 'center' }}>Canonical</th>
                                <th style={{ textAlign: 'center' }}>Robots</th>
                                <th style={{ textAlign: 'center' }}>Sitemap</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.map(r => (
                                <tr key={r.id}>
                                    <td style={{ fontWeight: 600 }}>{r.page_title || r.page_id}</td>
                                    <td><GlassBadge variant="info">{PAGE_TYPE_LABELS[r.page_type] || r.page_type}</GlassBadge></td>
                                    <td style={{ textAlign: 'center' }}><Tick ok={r.canonical_ok} /></td>
                                    <td style={{ textAlign: 'center' }}><Tick ok={r.robots_ok} /></td>
                                    <td style={{ textAlign: 'center' }}><Tick ok={r.sitemap_ok} /></td>
                                    <td>
                                        {r.page_url && (
                                            <a href={r.page_url} target="_blank" rel="noopener noreferrer" className="seo-index-link" title="Open page">
                                                <ExternalLink size={13} />
                                            </a>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </GlassCard>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Tab: Performance (Google Search Console)
// ---------------------------------------------------------------------------

const PerformanceTab: React.FC<{ projectId: string; gscJustConnected: boolean }> = ({ projectId, gscJustConnected }) => {
    const [status, setStatus] = useState<GscStatus | null>(null);
    const [sites, setSites] = useState<Array<{ url: string; permission: string }>>([]);
    const [selectedSite, setSelectedSite] = useState('');
    const [snapshots, setSnapshots] = useState<GscSnapshots | null>(null);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [showSitePicker, setShowSitePicker] = useState(false);
    const [error, setError] = useState('');

    const loadStatus = useCallback(async () => {
        try {
            const s = await gscGetStatus(projectId);
            setStatus(s);
            if (s.connected && s.site_url) {
                const data = await gscGetSnapshots(projectId, days);
                setSnapshots(data);
            } else if (s.status === 'connected' && !s.site_url) {
                // Connected but no site chosen yet — show site picker
                const { sites: siteList } = await gscListSites(projectId);
                setSites(siteList);
                setShowSitePicker(true);
            }
        } catch { /* disconnected state */ }
        setLoading(false);
    }, [projectId, days]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    useEffect(() => {
        if (gscJustConnected && status?.status === 'connected' && !status?.site_url) {
            gscListSites(projectId).then(({ sites: sl }) => { setSites(sl); setShowSitePicker(true); }).catch(() => {});
        }
    }, [gscJustConnected, status, projectId]);

    const handleConnect = async () => {
        setConnecting(true);
        try {
            const { auth_url } = await gscGetAuthUrl(projectId);
            window.location.href = auth_url;
        } catch (e: any) {
            setError('Failed to start Google authorisation. Check server config.');
            setConnecting(false);
        }
    };

    const handleSiteConnect = async () => {
        if (!selectedSite) return;
        setConnecting(true);
        try {
            await gscConnect(projectId, selectedSite);
            setShowSitePicker(false);
            await loadStatus();
        } catch (e: any) { setError('Failed to save site. Please try again.'); }
        setConnecting(false);
    };

    const handleSync = async () => {
        setSyncing(true);
        try { await gscSync(projectId); await loadStatus(); } catch {}
        setSyncing(false);
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Disconnect Google Search Console? Historical data will be retained.')) return;
        await gscDisconnect(projectId);
        setStatus(null); setSnapshots(null);
    };

    if (loading) return <LoadingState message="Loading performance data..." />;

    // Not connected
    if (!status?.connected && status?.status !== 'needs_reauth') {
        return (
            <GlassCard style={{ textAlign: 'center', padding: '48px 24px' }}>
                <TrendingUp size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
                <h3 style={{ color: 'var(--text)', marginBottom: 8 }}>Connect Google Search Console</h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: 24, maxWidth: 480, margin: '0 auto 24px' }}>
                    See which queries drive traffic to your pages — impressions, clicks, and average position — pulled nightly from Google.
                </p>
                {error && <ErrorState message={error} />}
                <GlassButton variant="primary" onClick={handleConnect} disabled={connecting}>
                    <Wifi size={16} /> {connecting ? 'Redirecting...' : 'Connect Google Search Console'}
                </GlassButton>
            </GlassCard>
        );
    }

    // Needs reauth
    if (status?.status === 'needs_reauth') {
        return (
            <GlassCard style={{ textAlign: 'center', padding: '48px 24px' }}>
                <WifiOff size={48} style={{ color: 'var(--danger)', marginBottom: 16 }} />
                <h3 style={{ color: 'var(--text)', marginBottom: 8 }}>Re-authorise Google Search Console</h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: 8 }}>Your Google authorisation has expired or been revoked.</p>
                {status.last_error && <p style={{ color: 'var(--danger)', fontSize: '0.8rem', marginBottom: 16 }}>{status.last_error.slice(0, 200)}</p>}
                <GlassButton variant="primary" onClick={handleConnect} disabled={connecting}>
                    <RefreshCw size={16} /> Re-authorise
                </GlassButton>
            </GlassCard>
        );
    }

    // Site picker (connected, no site chosen yet)
    if (showSitePicker) {
        return (
            <GlassCard>
                <h3 style={{ color: 'var(--text)', marginBottom: 8 }}>Choose a verified site</h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>Select which site to pull data from.</p>
                <select value={selectedSite} onChange={e => setSelectedSite(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--stroke)', borderRadius: 8, background: 'var(--panel)', color: 'var(--text)', marginBottom: 16 }}>
                    <option value="">Select a site...</option>
                    {sites.map(s => <option key={s.url} value={s.url}>{s.url}</option>)}
                </select>
                {error && <ErrorState message={error} />}
                <GlassButton variant="primary" onClick={handleSiteConnect} disabled={!selectedSite || connecting}>
                    {connecting ? 'Connecting...' : 'Connect this site'}
                </GlassButton>
            </GlassCard>
        );
    }

    // Performance data
    return (
        <>
            {/* Header bar */}
            <GlassCard style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Wifi size={16} style={{ color: 'var(--success)' }} />
                        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{status?.site_url}</span>
                        {status?.last_synced && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Last synced {new Date(status.last_synced).toLocaleDateString()}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <select value={days} onChange={e => setDays(Number(e.target.value))}
                            style={{ padding: '6px 10px', border: '1px solid var(--stroke)', borderRadius: 6, background: 'var(--panel)', color: 'var(--text)', fontSize: '0.8rem' }}>
                            <option value={7}>7 days</option>
                            <option value={30}>30 days</option>
                            <option value={90}>90 days</option>
                        </select>
                        <GlassButton variant="secondary" onClick={handleSync} disabled={syncing} style={{ fontSize: '0.8rem', padding: '6px 12px' }}>
                            <RefreshCw size={13} /> {syncing ? 'Syncing...' : 'Sync now'}
                        </GlassButton>
                        <GlassButton variant="secondary" onClick={handleDisconnect} style={{ fontSize: '0.8rem', padding: '6px 12px', color: 'var(--danger)' }}>
                            Disconnect
                        </GlassButton>
                    </div>
                </div>
            </GlassCard>

            {!snapshots || snapshots.daily_totals.length === 0 ? (
                <GlassCard style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-secondary)' }}>
                    <TrendingUp size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                    <p>No data yet for the selected period. Data syncs nightly — check back tomorrow.</p>
                </GlassCard>
            ) : (
                <>
                    {/* KPI summary */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
                        {[
                            { label: 'Total Clicks', value: snapshots.daily_totals.reduce((a, r) => a + r.clicks, 0).toLocaleString() },
                            { label: 'Total Impressions', value: snapshots.daily_totals.reduce((a, r) => a + r.impressions, 0).toLocaleString() },
                            { label: 'Avg Position', value: (snapshots.daily_totals.reduce((a, r) => a + (r.avg_position || 0), 0) / snapshots.daily_totals.length).toFixed(1) },
                        ].map(m => (
                            <GlassCard key={m.label} style={{ textAlign: 'center', padding: '20px 16px' }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{m.label}</div>
                                <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--primary-500)' }}>{m.value}</div>
                            </GlassCard>
                        ))}
                    </div>

                    {/* Top queries */}
                    <GlassCard className="seo-results-card">
                        <div className="seo-results-section">
                            <h3>Top Queries ({snapshots.top_queries.length})</h3>
                            <table className="seo-keyword-table">
                                <thead><tr><th>Query</th><th style={{ textAlign: 'right' }}>Clicks</th><th style={{ textAlign: 'right' }}>Impressions</th><th style={{ textAlign: 'right' }}>Avg Position</th></tr></thead>
                                <tbody>
                                    {snapshots.top_queries.map((q, i) => (
                                        <tr key={i}>
                                            <td>{q.query}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{q.clicks.toLocaleString()}</td>
                                            <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{q.impressions.toLocaleString()}</td>
                                            <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{q.avg_position?.toFixed(1)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="seo-results-section">
                            <h3>Top Pages ({snapshots.top_pages.length})</h3>
                            <table className="seo-keyword-table">
                                <thead><tr><th>Page</th><th style={{ textAlign: 'right' }}>Clicks</th><th style={{ textAlign: 'right' }}>Impressions</th></tr></thead>
                                <tbody>
                                    {snapshots.top_pages.map((p, i) => (
                                        <tr key={i}>
                                            <td style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                                <a href={p.page} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-500)' }}>{p.page}</a>
                                            </td>
                                            <td style={{ textAlign: 'right', fontWeight: 600 }}>{p.clicks.toLocaleString()}</td>
                                            <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{p.impressions.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </GlassCard>
                </>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Recent Searches panel
// ---------------------------------------------------------------------------

interface RecentSearchesPanelProps {
    records: SeoSearchRecord[];
    onRestore: (record: SeoSearchRecord) => void;
}

const RecentSearchesPanel: React.FC<RecentSearchesPanelProps> = ({ records, onRestore }) => {
    if (records.length === 0) return null;

    const queryLabel = (r: SeoSearchRecord): string => {
        const q = r.query_params;
        if (r.search_type === 'keywords') return q.topic || '';
        if (r.search_type === 'brief') return q.keyword || '';
        if (r.search_type === 'checker') return q.target_keyword || '';
        if (r.search_type === 'meta') return q.title || '';
        if (r.search_type === 'schema') return `${q.schema_type} schema`;
        return '';
    };

    const tabLabel = (type: string) => TABS.find(t => t.id === type)?.label || type;

    return (
        <GlassCard className="seo-history-panel">
            <div className="seo-history-header">
                <Clock size={14} />
                <span>Recent Searches</span>
            </div>
            <div className="seo-history-list">
                {records.slice(0, 8).map(r => (
                    <button key={r.id} className="seo-history-item" onClick={() => onRestore(r)} title={`Restore: ${queryLabel(r)}`}>
                        <span className="seo-history-icon">{TAB_ICONS[r.search_type]}</span>
                        <span className="seo-history-label">
                            <span className="seo-history-query">{queryLabel(r)}</span>
                            <span className="seo-history-meta">{tabLabel(r.search_type)} · {relativeTime(r.created_at)}</span>
                        </span>
                        <RotateCcw size={12} className="seo-history-restore-icon" />
                    </button>
                ))}
            </div>
        </GlassCard>
    );
};

// ---------------------------------------------------------------------------
// Main Module
// ---------------------------------------------------------------------------

const SEOModule: React.FC = () => {
    const ctx = useOutletContext<{ selectedProjectId?: string; onProjectSelect?: (id: string) => void }>();
    const selectedProjectId = ctx?.selectedProjectId;
    const onProjectSelect = ctx?.onProjectSelect;
    const [searchParams, setSearchParams] = useSearchParams();
    const gscJustConnected = searchParams.get('gsc') === 'connected';
    const [activeTab, setActiveTab] = useState<string>(() => {
        // URL param takes highest priority
        if (gscJustConnected) return 'performance';
        // Then localStorage
        const stored = localStorage.getItem('aime_seo_active_tab');
        if (stored && VALID_TAB_IDS.has(stored)) return stored;
        return 'keywords';
    });

    // Persist active tab to localStorage on every change
    useEffect(() => {
        localStorage.setItem('aime_seo_active_tab', activeTab);
    }, [activeTab]);

    // URL → sidebar: if ?project=X is in the URL and differs from the sidebar, sync it
    useEffect(() => {
        const urlProjectId = searchParams.get('project');
        if (urlProjectId && urlProjectId !== selectedProjectId && onProjectSelect) {
            onProjectSelect(urlProjectId);
        }
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sidebar → URL: when the selected project changes, reflect it in the URL
    useEffect(() => {
        if (selectedProjectId && searchParams.get('project') !== selectedProjectId) {
            setSearchParams({ project: selectedProjectId }, { replace: true });
        }
    }, [selectedProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Persisted results per tab — survive tab switching
    const [tabResults, setTabResults] = useState<Record<string, { query: Record<string, any>; result: any } | null>>({});
    const [history, setHistory] = useState<SeoSearchRecord[]>([]);
    const prevProjectId = useRef<string | undefined>(undefined);

    // Load history when project changes
    useEffect(() => {
        if (!selectedProjectId || selectedProjectId === prevProjectId.current) return;
        prevProjectId.current = selectedProjectId;
        setTabResults({});
        seoListSearches(selectedProjectId)
            .then(records => {
                setHistory(records);
                // Pre-populate each tab with its most recent result for this project
                const latest: Record<string, SeoSearchRecord> = {};
                records.forEach(r => { if (!latest[r.search_type]) latest[r.search_type] = r; });
                const restored: Record<string, { query: Record<string, any>; result: any }> = {};
                Object.entries(latest).forEach(([type, rec]) => {
                    restored[type] = { query: rec.query_params, result: rec.result_data };
                });
                setTabResults(restored);
            })
            .catch(() => {});
    }, [selectedProjectId]);

    const handleResult = useCallback((tabId: string, query: Record<string, any>, result: any) => {
        setTabResults(prev => ({ ...prev, [tabId]: { query, result } }));
        if (selectedProjectId) {
            seoSaveSearch({ project_id: selectedProjectId, search_type: tabId, query_params: query, result_data: result })
                .then(() => seoListSearches(selectedProjectId))
                .then(setHistory)
                .catch(() => {});
        }
    }, [selectedProjectId]);

    const handleRestore = useCallback((record: SeoSearchRecord) => {
        setTabResults(prev => ({ ...prev, [record.search_type]: { query: record.query_params, result: record.result_data } }));
        setActiveTab(record.search_type);
    }, []);

    // Promote a keyword from research → content brief tab, pre-filled and auto-submitted
    const handlePromoteKeyword = useCallback((keyword: string, industry: string) => {
        setTabResults(prev => ({ ...prev, brief: { query: { keyword, industry }, result: null } }));
        setActiveTab('brief');
    }, []);

    // Schema Coverage widget state
    const [coverage, setCoverage] = useState<SchemaCoverageResponse | null>(null);
    const [coverageLoading, setCoverageLoading] = useState(false);
    const [coverageExpanded, setCoverageExpanded] = useState(false);

    // Fetch coverage when schema tab becomes active or project changes
    useEffect(() => {
        if (activeTab !== 'schema' || !selectedProjectId) return;
        if (coverage !== null) return; // already loaded for this project
        setCoverageLoading(true);
        getSchemaCoverage(selectedProjectId)
            .then(data => setCoverage(data))
            .catch(() => setCoverage(null))
            .finally(() => setCoverageLoading(false));
    }, [activeTab, selectedProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset coverage when project changes so it refreshes
    useEffect(() => {
        setCoverage(null);
    }, [selectedProjectId]);

    if (!selectedProjectId) {
        return (
            <div className="seo-page">
                <ModuleHeader title="SEO Tooling" breadcrumb={['SEO']} />
                <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
                    <Search size={64} style={{ opacity: 0.4, marginBottom: '16px' }} />
                    <h3 style={{ color: 'var(--text)', marginBottom: '8px' }}>Select a project</h3>
                    <p>Choose a project from the sidebar to use SEO tools</p>
                </div>
            </div>
        );
    }

    const saved = tabResults[activeTab] || null;

    return (
        <div className="seo-page">
            <ModuleHeader title="SEO Tooling" breadcrumb={['SEO']} />

            <RecentSearchesPanel records={history} onRestore={handleRestore} />

            <div className="seo-tabs-row">
                <GlassTabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />
            </div>

            {activeTab === 'keywords' && (
                <KeywordResearchTab
                    savedResult={saved?.result || null}
                    savedQuery={saved?.query || null}
                    projectId={selectedProjectId}
                    onResult={(q, r) => handleResult('keywords', q, r)}
                    onPromoteKeyword={handlePromoteKeyword}
                />
            )}
            {activeTab === 'brief' && (
                <ContentBriefTab
                    savedResult={saved?.result || null}
                    savedQuery={saved?.query || null}
                    projectId={selectedProjectId}
                    onResult={(q, r) => handleResult('brief', q, r)}
                />
            )}
            {activeTab === 'checker' && (
                <SEOCheckerTab
                    savedResult={saved?.result || null}
                    savedQuery={saved?.query || null}
                    onResult={(q, r) => handleResult('checker', q, r)}
                />
            )}
            {activeTab === 'meta' && (
                <MetaTagsTab
                    savedResult={saved?.result || null}
                    savedQuery={saved?.query || null}
                    onResult={(q, r) => handleResult('meta', q, r)}
                />
            )}
            {activeTab === 'schema' && (
                <>
                    {/* Schema Coverage widget — sits above the manual Gemini generator */}
                    <GlassCard className="seo-schema-coverage">
                        <div className="seo-schema-coverage-header">
                            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text)' }}>Schema Coverage</h3>
                            {selectedProjectId && (
                                <GlassButton
                                    variant="secondary"
                                    onClick={() => {
                                        setCoverage(null);
                                        setCoverageLoading(true);
                                        getSchemaCoverage(selectedProjectId)
                                            .then(data => setCoverage(data))
                                            .catch(() => setCoverage(null))
                                            .finally(() => setCoverageLoading(false));
                                    }}
                                >
                                    <RefreshCw size={14} />
                                </GlassButton>
                            )}
                        </div>

                        {coverageLoading && (
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Loading coverage data…</p>
                        )}

                        {!coverageLoading && !selectedProjectId && (
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Select a project to see schema coverage.</p>
                        )}

                        {!coverageLoading && coverage && (
                            <div className="seo-schema-coverage-stats">
                                <div className="seo-schema-coverage-stat">
                                    <span className="seo-schema-coverage-stat-label">Published items</span>
                                    <strong>{coverage.total_published}</strong>
                                </div>
                                <div className="seo-schema-coverage-stat">
                                    <span className="seo-schema-coverage-stat-label">With valid schema</span>
                                    <strong>{coverage.with_valid_schema}</strong>
                                </div>
                                <div className="seo-schema-coverage-stat">
                                    <span
                                        className={`seo-schema-coverage-stat-pill ${
                                            coverage.coverage_pct >= 90 ? 'pill-success'
                                            : coverage.coverage_pct >= 60 ? 'pill-warning'
                                            : 'pill-danger'
                                        }`}
                                    >
                                        {coverage.coverage_pct}% covered
                                    </span>
                                </div>
                            </div>
                        )}

                        {!coverageLoading && coverage && coverage.missing.length > 0 && (
                            <div style={{ marginTop: 12 }}>
                                <GlassButton
                                    variant="secondary"
                                    onClick={() => setCoverageExpanded(prev => !prev)}
                                >
                                    {coverageExpanded ? 'Hide' : 'View'} {coverage.missing.length} item{coverage.missing.length !== 1 ? 's' : ''} missing schema
                                </GlassButton>
                                {coverageExpanded && (
                                    <div className="seo-schema-coverage-list">
                                        {coverage.missing.map((item, i) => (
                                            <div key={i} className="seo-schema-coverage-item">
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                                    <div>
                                                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>{item.page_type}</span>
                                                        <p style={{ margin: '2px 0', fontSize: '0.875rem', color: 'var(--text)' }}>{item.page_title || 'Untitled'}</p>
                                                        <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{item.reason}</p>
                                                    </div>
                                                    {item.page_url && (
                                                        <a href={item.page_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-500, #2177FF)', flexShrink: 0 }}>
                                                            <ExternalLink size={14} />
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {!coverageLoading && coverage && coverage.missing.length === 0 && coverage.total_published > 0 && (
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 8 }}>All published items have valid schema.</p>
                        )}
                    </GlassCard>

                    {/* Existing manual Gemini-powered Schema Generator — unchanged */}
                    <SchemaGeneratorTab
                        savedResult={saved?.result || null}
                        savedQuery={saved?.query || null}
                        onResult={(q, r) => handleResult('schema', q, r)}
                    />
                </>
            )}
            {activeTab === 'audit' && (
                <AuditTab projectId={selectedProjectId} />
            )}
            {activeTab === 'indexability' && (
                <IndexabilityTab projectId={selectedProjectId} />
            )}
            {activeTab === 'performance' && (
                <PerformanceTab projectId={selectedProjectId} gscJustConnected={gscJustConnected} />
            )}
            {activeTab === 'opportunities' && (
                <>
                    <StrikingDistancePanel projectId={selectedProjectId} />
                    <LowCtrPanel projectId={selectedProjectId} />
                </>
            )}
            {activeTab === 'cannibalization' && (
                <CannibalizationPanel projectId={selectedProjectId} />
            )}
            {activeTab === 'ai-referrals' && (
                <AIReferrerPanel projectId={selectedProjectId} />
            )}
            {activeTab === 'index-coverage' && (
                <IndexCoveragePanel projectId={selectedProjectId} />
            )}
            {activeTab === 'local-categories' && (
                <GbpCategoryGapPanel projectId={selectedProjectId} />
            )}
            {activeTab === 'citations' && (
                <CitationAuditPanel projectId={selectedProjectId} />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// AuditTab sub-component
// ---------------------------------------------------------------------------

const AUDIT_POLL_INTERVAL_MS = 2000;
const AUDIT_POLL_CEILING_MS  = 90_000;
const AUDIT_LS_ACTIVE_ID     = 'aime_seo_active_audit_id';

interface AuditTabProps {
    projectId: string;
}

const AuditTab: React.FC<AuditTabProps> = ({ projectId }) => {
    const [url, setUrl] = useState('');
    const [auditRecord, setAuditRecord] = useState<SeoAuditRecord | null>(null);
    const [history, setHistory] = useState<SeoAuditHistoryItem[]>([]);
    const [polling, setPolling] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [maxPagesUsed, setMaxPagesUsed] = useState<number | null>(null);
    const [auditMigration, setAuditMigration] = useState<MigrationCheckResult | null>(null);

    const pollTimerRef  = useRef<ReturnType<typeof setInterval> | null>(null);
    const pollStartRef  = useRef<number>(0);
    const activeAuditId = useRef<string | null>(null);

    // Load history on mount / project change
    useEffect(() => {
        listSeoAuditHistory(projectId)
            .then(setHistory)
            .catch(() => {});

        // Re-attach to any in-flight audit from a previous page load
        const savedId = localStorage.getItem(AUDIT_LS_ACTIVE_ID);
        if (savedId) {
            getSeoAudit(savedId)
                .then(rec => {
                    if (rec.status === 'running') {
                        activeAuditId.current = savedId;
                        setPolling(true);
                        pollStartRef.current = Date.now();
                    } else {
                        localStorage.removeItem(AUDIT_LS_ACTIVE_ID);
                        setAuditRecord(rec);
                        fetchMigration(projectId);
                    }
                })
                .catch(() => localStorage.removeItem(AUDIT_LS_ACTIVE_ID));
        }
    }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Polling loop
    useEffect(() => {
        if (!polling) {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            return;
        }
        pollTimerRef.current = setInterval(async () => {
            if (!activeAuditId.current) return;
            if (Date.now() - pollStartRef.current > AUDIT_POLL_CEILING_MS) {
                stopPolling('Audit timed out waiting for results. Please try again.');
                return;
            }
            try {
                const rec = await getSeoAudit(activeAuditId.current);
                if (rec.status !== 'running') {
                    stopPolling(null);
                    setAuditRecord(rec);
                    localStorage.removeItem(AUDIT_LS_ACTIVE_ID);
                    // Refresh history
                    listSeoAuditHistory(projectId).then(setHistory).catch(() => {});
                    // Fetch migration check best-effort
                    fetchMigration(projectId);
                }
            } catch {
                stopPolling('Lost connection while waiting for audit results.');
            }
        }, AUDIT_POLL_INTERVAL_MS);
        return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
    }, [polling, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

    function stopPolling(errMsg: string | null) {
        setPolling(false);
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        if (errMsg) setError(errMsg);
    }

    // Best-effort fetch migration check whenever an audit result is shown.
    // On error or has_gsc_history=false the panel simply doesn't render.
    function fetchMigration(pid: string) {
        getMigrationCheck(pid)
            .then(r => setAuditMigration(r.has_gsc_history ? r : null))
            .catch(() => setAuditMigration(null));
    }

    async function startAudit(auditUrl: string) {
        setError(null);
        setAuditRecord(null);
        setAuditMigration(null);
        try {
            const res = await runSeoAudit({ url: auditUrl, project_id: projectId });
            activeAuditId.current = res.audit_id;
            localStorage.setItem(AUDIT_LS_ACTIVE_ID, res.audit_id);
            setMaxPagesUsed(res.max_pages_used);
            setPolling(true);
            pollStartRef.current = Date.now();
        } catch (e: any) {
            setError(getApiError(e, 'Failed to start audit. Please check the URL and try again.'));
        }
    }

    function handleRunAudit() {
        if (!url.trim()) return;
        startAudit(url.trim());
    }

    function handleReaudit() {
        if (auditRecord) startAudit(auditRecord.root_url);
    }

    function handleCancel() {
        activeAuditId.current = null;
        localStorage.removeItem(AUDIT_LS_ACTIVE_ID);
        stopPolling(null);
    }

    async function handleViewHistoric(auditId: string) {
        try {
            const rec = await getSeoAudit(auditId);
            setAuditRecord(rec);
            setError(null);
            // Best-effort migration check for historical audits
            fetchMigration(projectId);
        } catch {
            setError('Failed to load the audit record.');
        }
    }

    // Inline SVG sparkline for trend chart
    const renderSparkline = () => {
        const pts = history
            .filter(h => h.score_total !== null)
            .slice(0, 20)
            .reverse();
        if (pts.length < 2) return null;
        const W = 240, H = 48, pad = 4;
        const scores = pts.map(p => p.score_total as number);
        const minS = Math.min(...scores), maxS = Math.max(...scores);
        const range = maxS - minS || 1;
        const toX = (i: number) => pad + (i / (pts.length - 1)) * (W - pad * 2);
        const toY = (s: number) => H - pad - ((s - minS) / range) * (H - pad * 2);
        const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(p.score_total as number).toFixed(1)}`).join(' ');
        return (
            <svg width={W} height={H} className="sa-audit-sparkline">
                <polyline points={pts.map((p, i) => `${toX(i).toFixed(1)},${toY(p.score_total as number).toFixed(1)}`).join(' ')} />
            </svg>
        );
    };

    return (
        <div className="sa-audit-tab">
            {/* URL input form */}
            {!auditRecord && (
                <GlassCard>
                    <h3 style={{ margin: '0 0 16px', fontSize: '1rem', color: 'var(--text)' }}>Run a Site Audit</h3>
                    {maxPagesUsed && (
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginBottom: 12 }}>
                            Your plan audits up to {maxPagesUsed} pages.
                        </p>
                    )}
                    <div className="sa-audit-form">
                        <GlassInput
                            placeholder="https://yoursite.com"
                            value={url}
                            onChange={setUrl}
                            type="url"
                        />
                        {!polling ? (
                            <GlassButton variant="primary" onClick={handleRunAudit} disabled={!url.trim()}>
                                Run Audit
                            </GlassButton>
                        ) : (
                            <GlassButton variant="secondary" onClick={handleCancel}>
                                Cancel
                            </GlassButton>
                        )}
                    </div>
                    {polling && (
                        <div className="sa-audit-polling">
                            <Clock size={14} />
                            <span>Auditing site… this may take up to 45 seconds</span>
                        </div>
                    )}
                    {error && (
                        <div className="sa-audit-error">
                            <AlertCircle size={14} />
                            <span>{error}</span>
                        </div>
                    )}
                </GlassCard>
            )}

            {/* Audit result */}
            {auditRecord && (
                <>
                    <div style={{ marginBottom: 8 }}>
                        <GlassButton variant="secondary" onClick={() => { setAuditRecord(null); setError(null); }}>
                            ← New Audit
                        </GlassButton>
                    </div>
                    <AuditReport
                        audit={auditRecord}
                        showSignupCta={false}
                        onReaudit={handleReaudit}
                        migrationCheck={auditMigration}
                    />
                </>
            )}

            {/* History panel */}
            {history.length > 0 && !auditRecord && (
                <GlassCard>
                    <div className="sa-audit-history-header">
                        <h3 style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text)' }}>Audit History</h3>
                        {renderSparkline()}
                    </div>
                    <div className="sa-audit-history-list">
                        {history.map(h => (
                            <button
                                key={h.audit_id}
                                className="sa-audit-history-row"
                                onClick={() => handleViewHistoric(h.audit_id)}
                                title={`View audit for ${h.root_url}`}
                            >
                                <span className="sa-audit-history-url">{h.root_url}</span>
                                <span className={`sa-audit-history-score ${h.score_total !== null ? (h.score_total >= 80 ? 'excellent' : h.score_total >= 60 ? 'good' : h.score_total >= 40 ? 'fair' : 'poor') : ''}`}>
                                    {h.score_total !== null ? h.score_total : '—'}
                                </span>
                                <span className="sa-audit-history-date">
                                    {new Date(h.created_at).toLocaleDateString()}
                                </span>
                                <ChevronRight size={14} />
                            </button>
                        ))}
                    </div>
                </GlassCard>
            )}
        </div>
    );
};

export default SEOModule;
